import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import axios from 'axios';
import { attachAxiosSentryHandlers } from '@/observability/sentry.axios';

export const Route = createFileRoute('/_authenticated/admin/office')({
  component: AdminOfficePage,
});

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const officeApi = axios.create({
  baseURL: API,
});

attachAxiosSentryHandlers(officeApi);

function getHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

function AdminOfficePage() {
  const { user, token } = useAuthStore();
  const [tab, setTab] = useState<'map' | 'users' | 'stats'>('map');
  const tenantId = user?.tenantId ?? '';
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [addOpen, setAddOpen] = useState(false);
  const [deskLabel, setDeskLabel] = useState('');

  useQuery({
    queryKey: ['office-room', tenantId],
    queryFn: () =>
      officeApi
        .get(`/office/${tenantId}/room`, { headers: getHeaders(token!) })
        .then((r) => r.data),
    enabled: !!tenantId && !!token,
  });

  const { data: desks = [] } = useQuery({
    queryKey: ['office-desks', tenantId],
    queryFn: () =>
      officeApi
        .get(`/office/${tenantId}/desks`, { headers: getHeaders(token!) })
        .then((r) => r.data),
    enabled: !!tenantId && !!token,
  });

  const { data: stats } = useQuery({
    queryKey: ['office-stats', tenantId],
    queryFn: () =>
      officeApi
        .get(`/office/${tenantId}/stats`, { headers: getHeaders(token!) })
        .then((r) => r.data),
    enabled: !!tenantId && !!token && tab === 'stats',
    refetchInterval: tab === 'stats' ? 5000 : false,
  });

  const addDeskMutation = useMutation({
    mutationFn: (body: { label: string; x: number; y: number }) =>
      officeApi.post(`/office/${tenantId}/desks`, body, {
        headers: getHeaders(token!),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['office-desks', tenantId] });
      setAddOpen(false);
    },
    // axios cru (sem interceptor global): precisa do próprio toast
    onError: () => toast.error('Não foi possível adicionar a mesa'),
  });

  const removeDeskMutation = useMutation({
    mutationFn: (deskId: string) =>
      officeApi.delete(`/office/${tenantId}/desks/${deskId}`, {
        headers: getHeaders(token!),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['office-desks', tenantId] }),
    onError: () => toast.error('Não foi possível remover a mesa'),
  });

  const tabs = [
    { key: 'map', label: '🗺 Mapa' },
    { key: 'users', label: '👥 Usuários' },
    { key: 'stats', label: '📊 Estatísticas' },
  ] as const;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-4 text-xl font-bold">⚙️ Admin — Escritório Virtual</h1>

      {/* Tabs */}
      <div className="mb-6 flex border-b">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Aba Mapa */}
      {tab === 'map' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Mesas cadastradas ({desks.length})</h2>
            <button
              onClick={() => {
                setDeskLabel('');
                setAddOpen(true);
              }}
              className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-500"
            >
              + Adicionar Mesa
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {desks.map((desk: any) => (
              <div
                key={desk.id}
                className="flex items-center justify-between rounded-lg bg-muted px-4 py-3"
              >
                <div>
                  <div className="text-sm font-medium">{desk.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {desk.occupiedBy ? `Ocupada` : 'Livre'}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: `Remover mesa "${desk.label}"?`,
                        confirmLabel: 'Remover mesa',
                        destructive: true,
                      })
                    ) {
                      removeDeskMutation.mutate(desk.id);
                    }
                  }}
                  className="text-xs text-red-500 hover:text-red-400"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>

          {desks.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma mesa cadastrada. Clique em "+ Adicionar Mesa" para começar.
            </p>
          )}
        </div>
      )}

      {/* Aba Usuários */}
      {tab === 'users' && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Gerencie quais usuários têm acesso ao escritório virtual.
          </p>
          <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
            Todos os usuários ativos do tenant têm acesso automaticamente via JWT.
            Controle de acesso granular estará disponível em versões futuras.
          </div>
        </div>
      )}

      {/* Aba Estatísticas */}
      {tab === 'stats' && (
        <div className="space-y-4">
          {stats ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-muted p-4">
                  <div className="text-2xl font-bold">{stats.onlineCount}</div>
                  <div className="text-xs text-muted-foreground">
                    Usuários online agora
                  </div>
                </div>
              </div>
              <h3 className="text-sm font-semibold">Quem está online</h3>
              {stats.players.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Ninguém online no momento.
                </p>
              ) : (
                <div className="space-y-2">
                  {stats.players.map((p: any) => (
                    <div
                      key={p.userId}
                      className="flex items-center gap-3 rounded bg-muted px-3 py-2"
                    >
                      <div className="h-2 w-2 rounded-full bg-green-400" />
                      <span className="text-sm">{p.name}</span>
                      {p.deskId && (
                        <span className="text-xs text-amber-400">💺 na mesa</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Carregando estatísticas...</p>
          )}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent size="sm" busy={addDeskMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Adicionar mesa</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!deskLabel.trim()) return;
              addDeskMutation.mutate({ label: deskLabel.trim(), x: 200, y: 200 });
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="desk-label">Nome da mesa *</Label>
              <Input
                id="desk-label"
                value={deskLabel}
                onChange={(e) => setDeskLabel(e.target.value)}
                placeholder="Dev 1"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={addDeskMutation.isPending} disabled={!deskLabel.trim()}>
                Adicionar mesa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
